<template>
  <main class="wallet-page">
    <AppNavBar :title="t('home.topup')" />

    <section class="wallet-card selector-card" @click="goSelectCoin">
      <div class="coin-title">
        <app-image :src="selectedCoin.logo || usdtIcon" alt="" />
        <div data-strong>{{ selectedCoin.symbol }}</div>
      </div>
      <span>
        {{ t("wallet.chooseCoin") }}
        <i class="selector-card__arrow" aria-hidden="true" />
      </span>
    </section>

    <section class="wallet-card network-card" v-if="chains.length">
      <h2>{{ t("wallet.chooseNetwork") }}</h2>
      <div class="network-select" @click.stop="toggleChainPopup">
        <span>{{ selectedChainLabel }}</span>
        <van-icon name="arrow-down" class="network-select__arrow" />
      </div>
      <div v-if="showChainPopup" class="network-options">
        <div
          v-for="option in chainOptions"
          :key="`${option.text}-${option.value}`"
          :class="[
            'network-options__item',
            {
              'network-options__item--active':
                selectedChainIndex === option.value,
            },
          ]"
          @click.stop="selectChain(option.value)"
        >
          {{ option.text }}
        </div>
      </div>
    </section>

    <div
      v-if="showChainPopup"
      class="network-mask"
      @click="showChainPopup = false"
    />

    <section class="qr-card">
      <div class="qr-card__box">
        <app-image v-if="qrUrl" :src="qrUrl" alt="" />
      </div>
      <p>{{ chargeAddress || "--" }}</p>
      <van-button block round type="primary" @click="copyAddress">
        <div class="wallet">
          <span>{{ t("wallet.copyAddress") }}</span>
          <app-image class="copy-address-icon" :src="copyIconWhite" alt="" />
        </div>
      </van-button>
    </section>

    <section class="wallet-card notes-card">
      <h2>{{ t("wallet.topupNotes") }}</h2>
      <p>
        {{ t("wallet.topupNote1Prefix") }}
        <span data-strong>{{ t("wallet.topupNote1Highlight1") }}</span>
        {{ t("wallet.topupNote1Middle") }}
        <span data-strong>{{ t("wallet.topupNote1Highlight2") }}</span>
        {{ t("wallet.topupNote1Suffix") }}
      </p>
      <p>{{ t("wallet.topupNote2") }}</p>
      <p>{{ t("wallet.topupNote3") }}</p>
    </section>
  </main>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import QRCode from "qrcode";
import { showFailToast, showLoadingToast, closeToast, showToast } from "vant";
import { showPassedToast } from "@/utils/toast";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useProfileStore } from "@/stores/profile";
import { useWalletTopupStore } from "@/stores/wallet-topup";
import { copyToClipboard } from "@/utils/clipboard";
import usdtIcon from "@/assets/images/usdt.png";
import copyIconWhite from "@/assets/images/common_copy_icon_white.png";

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();
const walletTopupStore = useWalletTopupStore();
const selectedChainIndex = ref(0);
const showChainPopup = ref(false);
const qrUrl = ref("");
const selectedCoin = computed(
  () =>
    walletTopupStore.selectedCoin || {
      symbol: "USDT",
      logo: "",
      chains: [
        {
          symbolType: "BEP20",
          chargeAddress:
            profileStore.currentAddress ||
            "0x0000000000000000000000000000000000000000",
        },
      ],
    },
);
const chains = computed(() => selectedCoin.value.chains || []);
const selectedChain = computed(
  () => chains.value[selectedChainIndex.value] || {},
);
const chargeAddress = computed(() => selectedChain.value.chargeAddress || "");
const chainOptions = computed(() =>
  chains.value.map((item, index) => ({
    text: item.symbolType || item.chainName || `Network ${index + 1}`,
    value: index,
  })),
);
const selectedChainLabel = computed(
  () => chainOptions.value[selectedChainIndex.value]?.text || "--",
);

watch(
  () => walletTopupStore.selectedCoinIndex,
  () => {
    selectedChainIndex.value = 0;
    showChainPopup.value = false;
  },
);

function toggleChainPopup() {
  if (chainOptions.value.length <= 1) {
    return;
  }
  showChainPopup.value = !showChainPopup.value;
}

function selectChain(index) {
  selectedChainIndex.value = index;
  showChainPopup.value = false;
}

async function copyAddress() {
  if (!chargeAddress.value) return;
  const copied = await copyToClipboard(chargeAddress.value);
  if (copied) {
    showPassedToast(t("wallet.copySuccess"));
    return;
  }
  showFailToast(t("wallet.copyFailed"));
}

function goSelectCoin() {
  router.push("/wallet/topup/select-coin");
}

async function loadData() {
  showLoadingToast({
    message: t("common.loading"),
    forbidClick: true,
    duration: 0,
  });
  try {
    await walletTopupStore.fetchChargeInfo({
      address: profileStore.platformAddress || null,
      reset: !walletTopupStore.list.length,
    });
    selectedChainIndex.value = 0;
    showChainPopup.value = false;
  } catch (error) {
    showFailToast(error?.msg || t("common.retryLater"));
  } finally {
    closeToast();
  }
}

watch(
  chargeAddress,
  async (value) => {
    qrUrl.value = value
      ? await QRCode.toDataURL(value, { margin: 1, width: 220 })
      : "";
  },
  { immediate: true },
);

onMounted(loadData);
</script>

<style scoped lang="scss">
.wallet-page {
  min-height: var(--app-viewport-height);
  padding: 15px 14px 34px;
  background: #f7f7f7;
}

.wallet-card {
  margin-bottom: 10px;
  padding: 13px 16px;
  border-radius: 16px;
  background: #fff;
}
.qr-card {
  margin-bottom: 10px;
  padding: 18px 16px;
  border-radius: 8px;
}

.network-card {
  position: relative;
  z-index: 3;
}

.selector-card {
  display: flex;
  align-items: center;
  justify-content: space-between;

  > span {
    display: flex;
    align-items: center;
    color: rgba(0, 0, 0, 0.65);
    font-size: 14px;

    .app-image {
      width: 29px;
      height: 29px;
      margin-left: 4px;
      object-fit: contain;
    }
  }

  &__arrow {
    width: 29px;
    height: 29px;
    margin-left: 4px;
    flex: 0 0 29px;
    background: var(--wp-color-theme);
    -webkit-mask: url("@/assets/images/img_account_right.png") center / contain
      no-repeat;
    mask: url("@/assets/images/img_account_right.png") center / contain
      no-repeat;
  }
}

.coin-title {
  display: flex;
  align-items: center;

  .app-image {
    width: 40px;
    height: 40px;
    margin-right: 8px;
    border-radius: 50%;
  }

  [data-strong] {
    font-size: 16px;
  }
}

h2 {
  margin: 0 0 14px;
  font-size: 16px;
  font-weight: 500;
}

.network-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  padding: 0 10px;
  border-radius: 18px;
  background: #f7f7f7;
  color: #1d2129;
  font-size: 16px;
  cursor: pointer;

  span {
    min-width: 0;
    flex: 1;
  }
}

.network-select__arrow {
  margin-left: 12px;
  flex: 0 0 auto;
  color: rgba(0, 0, 0, 0.55);
  font-size: 22px;
}

.network-options {
  position: absolute;
  top: 96px;
  left: 16px;
  right: 16px;
  overflow: hidden;
  border-radius: 24px;
  background: #ffffff;
  box-shadow:
    0 12px 28px rgba(0, 0, 0, 0.12),
    0 4px 12px rgba(0, 0, 0, 0.08);
  z-index: 5;

  &__item {
    width: 100%;
    padding: 10px 18px;
    border: 0;
    background: #ffffff;
    color: #1d2129;
    font-size: 14px;
    text-align: left;
  }

  &__item + &__item {
    border-top: 1px solid #f5f5f5;
  }

  &__item--active {
    background: #d9d9d9;
  }
}

.network-mask {
  position: fixed;
  inset: 0;
  z-index: 2;
  background: transparent;
}

.qr-card {
  text-align: center;
  .wallet {
    display: flex;
    align-items: center;
    color: #fff;
  }
}

.qr-card__box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 236px;
  height: 236px;
  margin: 0 auto;
  padding: 24px;
  border-radius: 12px;
  background: #ffffff;
}

.qr-card p {
  margin: 16px 0;
  color: var(--wp-color-text);
  font-size: 12px;
  font-weight: 500;
  overflow-wrap: anywhere;
}

.copy-address-icon {
  width: 16px;
  height: 16px;
  margin-left: 8px;
  flex: 0 0 16px;
  object-fit: contain;
}

.notes-card p {
  margin: 6px 0 0;
  color: #333333;
  font-size: 12px;
  line-height: 18px;

  [data-strong] {
    color: #000000;
    font-weight: 500;
  }
}
</style>
